import styled from '@emotion/styled'
import PropTypes from '~/lib/propTypes'
import { useFormik } from 'formik'
import useLoggedInUser from '~/hooks/useLoggedInUser'
import * as yup from 'yup'
import {
  Alert,
  DialogBody,
  DialogFooter,
  InputV2 as Input,
  Button,
  space,
  StarRating,
  Textarea,
  Instruction,
} from '@ticketswap/solar'
import { GIVE_USER_FEEDBACK } from '~/graphql/mutations/giveUserFeedback'
import { useState } from 'react'
import { useMutation } from '@apollo/client'

const InputWrapper = styled.div`
  display: grid;
  grid-gap: ${space[16]};
`

const Feedback = ({ t, setActiveView }) => {
  const [rating, setRating] = useState(0)
  const [error, setError] = useState(null)
  const { loggedInUser } = useLoggedInUser()
  const [send, { loading }] = useMutation(GIVE_USER_FEEDBACK)

  const getSentiment = () => {
    return rating === 0 ? null : rating < 3 ? 'NEGATIVE' : 'POSITIVE'
  }

  const isPositive = () => {
    return getSentiment() === 'POSITIVE'
  }

  const formik = useFormik({
    initialValues: {
      email: loggedInUser?.email || '',
      name: loggedInUser ? `${loggedInUser.firstname} ${loggedInUser.lastname}` : '',
      message: '',
    },
    validationSchema: yup.object().shape({
      name: yup.string().required(!loggedInUser),
      email: yup.string().email().required(!loggedInUser),
      message: yup.string().required('Required'),
    }),
    onSubmit: async values => {
      setError(null)
      try {
        const {
          data: { giveUserFeedback: response },
        } = await send({
          variables: {
            input: {
              ...values,
              rating: rating === 0 ? null : rating,
              sentiment: getSentiment(),
              url: global.location.href,
            },
          },
        })

        if (response.errors.length > 0) {
          setError(response.errors[0].message)
        }

        setActiveView(isPositive() ? 'positive-confirm' : 'negative-confirm')

        sessionStorage.setItem(
          isPositive() ? 'positive_feedback_submitted' : 'negative_feedback_submitted',
          true
        )
      } catch (error) {
        setError(error)
      }
    },
  })

  const { values, isValid, dirty, handleChange, handleSubmit } = formik
  const disabled =
    values.message.length < 3 || (loggedInUser && rating === 0) || !isValid || !dirty || loading

  return (
    <form onSubmit={handleSubmit}>
      <DialogBody>
        <InputWrapper>
          {error && <Alert variant="error">{error}</Alert>}

          {loggedInUser && (
            <StarRating
              label={t('common:feedbackDialog.fields.rating.label')}
              onChange={setRating}
            />
          )}

          {!loggedInUser && (
            <>
              <Input
                type="text"
                name="name"
                id="name"
                label={t('common:feedbackDialog.fields.name.label')}
                value={values.name}
                onChange={handleChange}
              />
              <Input
                type="email"
                name="email"
                id="email"
                label={t('common:feedbackDialog.fields.email.label')}
                value={values.email}
                onChange={handleChange}
              />
            </>
          )}

          <Textarea
            rows={6}
            name="message"
            id="message"
            label={t('common:feedbackDialog.fields.message.label')}
            value={values.message}
            onChange={handleChange}
            instruction={
              loggedInUser && (
                <Instruction>{t('common:feedbackDialog.fields.message.help')}</Instruction>
              )
            }
          />
        </InputWrapper>
      </DialogBody>
      <DialogFooter>
        <Button type="submit" fullWidth loading={loading} disabled={disabled}>
          {t('common:feedbackDialog.button')}
        </Button>
      </DialogFooter>
    </form>
  )
}

export default Feedback

Feedback.propTypes = {
  t: PropTypes.func.isRequired,
  setActiveView: PropTypes.func.isRequired,
}
